import type { CaravanDb } from './db'
import { LocalRepository } from './repository'
import {
  canSeePollResults,
  canVoteInPoll,
  pollStatusAt,
  validatePollInput,
  type PollInput,
  type PollResultVisibility,
  type PollSelectionMode,
  type PollStatus,
} from '../../domain/poll'
import type { LocalEntity } from '../../domain/sync'
import { MediaRepository } from './mediaRepository'
import type { ProcessedImageBundle } from '../../services/imagePipeline'

export type PollMediaInput = { bundle: ProcessedImageBundle; fileName: string }

export type PollOption = {
  id: string
  label: string
  sortOrder: number
}

export type PollRecord = {
  id: string
  tripId: string
  title: string
  description: string
  imageUrl: string | null
  place: string | null
  price: string | null
  selectionMode: PollSelectionMode
  resultVisibility: PollResultVisibility
  closesAt: string | null
  status: PollStatus
  options: PollOption[]
  creatorMemberId: string
  version: number
  syncState: LocalEntity['syncState']
}

export type PollOptionView = PollOption & { selected: boolean; voteCount: number | null }
export type PollView = Omit<PollRecord, 'options'> & {
  options: PollOptionView[]
  resultsVisible: boolean
  hasVoted: boolean
}

export type SetVoteInput = {
  pollId: string
  memberId: string
  optionId: string
  selected: boolean
  mutationId?: string
  now?: Date
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function fromEntity(entity: LocalEntity): PollRecord {
  const payload = entity.payload
  const optionIds = strings(payload.optionIds)
  const optionLabels = strings(payload.optionLabels)
  return {
    id: entity.id,
    tripId: entity.tripId,
    title: typeof payload.title === 'string' ? payload.title : '',
    description: typeof payload.description === 'string' ? payload.description : '',
    imageUrl: typeof payload.imageUrl === 'string' ? payload.imageUrl : null,
    place: typeof payload.place === 'string' ? payload.place : null,
    price: typeof payload.price === 'string' ? payload.price : null,
    selectionMode: payload.selectionMode as PollSelectionMode,
    resultVisibility: payload.resultVisibility as PollResultVisibility,
    closesAt: typeof payload.closesAt === 'string' ? payload.closesAt : null,
    status: payload.status as PollStatus,
    options: optionIds.map((id, index) => ({ id, label: optionLabels[index] ?? '', sortOrder: index })),
    creatorMemberId: typeof payload.creatorMemberId === 'string' ? payload.creatorMemberId : '',
    version: entity.version,
    syncState: entity.syncState,
  }
}

export class PollRepository {
  readonly #db: CaravanDb
  readonly #local: LocalRepository
  readonly #media: MediaRepository
  readonly #localImageUrls = new Map<string, string>()

  constructor(db: CaravanDb) {
    this.#db = db
    this.#local = new LocalRepository(db)
    this.#media = new MediaRepository(db)
  }

  async listPolls(now = new Date()): Promise<PollRecord[]> {
    const rows = await this.#db.entities.where('entityType').equals('poll')
      .filter((entity) => entity.deletedAt === null)
      .toArray()
    const polls = rows.map(fromEntity).map((poll) => ({ ...poll, status: pollStatusAt(poll, now) }))
      .sort((left, right) => left.title.localeCompare(right.title, 'fr'))
    return Promise.all(polls.map(async (poll) => ({ ...poll, imageUrl: await this.#localImageUrl(poll) ?? poll.imageUrl })))
  }

  async createPoll(input: PollInput, creatorMemberId: string, media?: PollMediaInput): Promise<PollRecord> {
    const clean = validatePollInput(input)
    const id = crypto.randomUUID()
    const optionIds = clean.options.map(() => crypto.randomUUID())
    const createdAt = new Date().toISOString()
    if (media) await this.#media.storeBundle(id, media.bundle, createdAt)
    try {
      const entity = await this.#local.mutate({
        mutationId: crypto.randomUUID(),
        entityType: 'poll',
        entityId: id,
        tripId: clean.tripId,
        operation: 'create',
        baseVersion: 0,
        payload: {
          ...clean,
          options: undefined,
          optionIds,
          optionLabels: clean.options,
          creatorMemberId,
          status: 'open',
          imageLocalId: media ? id : null,
          imageFileName: media?.fileName ?? null,
          imageMetadata: media?.bundle.metadata ?? null,
        },
        createdAtDevice: createdAt,
      })
      const poll = fromEntity(entity)
      return { ...poll, imageUrl: await this.#localImageUrl(poll) ?? poll.imageUrl }
    } catch (error) {
      if (media) await this.#media.deleteUncommittedBundle(id)
      throw error
    }
  }

  async #localImageUrl(poll: PollRecord): Promise<string | null> {
    const cached = this.#localImageUrls.get(poll.id)
    if (cached) return cached
    const entity = await this.#db.entities.get(`poll:${poll.id}`)
    if (entity?.payload.imageLocalId !== poll.id) return null
    const bundle = await this.#media.getBundle(poll.id)
    if (!bundle || typeof URL.createObjectURL !== 'function') return null
    const url = URL.createObjectURL(bundle.thumbnail)
    this.#localImageUrls.set(poll.id, url)
    return url
  }

  async getMemberSelections(pollId: string, memberId: string): Promise<string[]> {
    const poll = await this.#db.entities.get(`poll:${pollId}`)
    const selections = new Set(strings(poll?.payload.remoteSelectedOptionIds))
    const rows = await this.#intentEntities(pollId, memberId)
    for (const entity of rows) {
      const optionId = entity.payload.optionId
      if (typeof optionId !== 'string') continue
      if (entity.payload.selected === true) selections.add(optionId)
      else selections.delete(optionId)
    }
    return [...selections].sort()
  }

  async setVote(input: SetVoteInput): Promise<void> {
    const pollEntity = await this.#db.entities.get(`poll:${input.pollId}`)
    if (!pollEntity) throw new Error('Vote introuvable.')
    const poll = fromEntity(pollEntity)
    if (!poll.options.some((option) => option.id === input.optionId)) throw new Error('Choix introuvable.')
    const now = input.now ?? new Date()
    if (!canVoteInPoll(poll, now)) throw new Error('Ce vote est clôturé : aucune nouvelle intention n’est acceptée.')
    const intents = await this.#intentEntities(input.pollId, input.memberId)

    if (poll.selectionMode === 'single' && input.selected) {
      for (const previous of intents.filter((entity) => entity.payload.optionId !== input.optionId && entity.payload.selected === true)) {
        await this.#writeIntent({
          poll,
          memberId: input.memberId,
          optionId: String(previous.payload.optionId),
          selected: false,
          entityId: previous.id,
          mutationId: crypto.randomUUID(),
          now,
        })
      }
    }
    const existing = intents.find((entity) => entity.payload.optionId === input.optionId)
    await this.#writeIntent({
      poll,
      memberId: input.memberId,
      optionId: input.optionId,
      selected: input.selected,
      entityId: existing?.id ?? crypto.randomUUID(),
      mutationId: input.mutationId ?? crypto.randomUUID(),
      now,
    })
  }

  async getPollView(pollId: string, memberId: string, isAdmin: boolean, now = new Date()): Promise<PollView> {
    const poll = (await this.listPolls(now)).find((candidate) => candidate.id === pollId)
    if (!poll) throw new Error('Vote introuvable.')
    const memberSelections = await this.getMemberSelections(pollId, memberId)
    const selected = new Set(memberSelections)
    const allIntents = await this.#db.entities.where('entityType').equals('vote_intent')
      .filter((entity) => entity.payload.pollId === pollId && entity.payload.selected === true)
      .toArray()
    const resultsVisible = canSeePollResults(poll, { hasVoted: selected.size > 0, isAdmin })
    const pollEntity = await this.#db.entities.get(`poll:${pollId}`)
    const remoteCounts = pollEntity?.payload.remoteVoteCounts
    const safeRemoteCounts = remoteCounts && typeof remoteCounts === 'object'
      ? remoteCounts as Record<string, unknown>
      : {}
    return {
      ...poll,
      resultsVisible,
      hasVoted: selected.size > 0,
      options: poll.options.map((option) => ({
        ...option,
        selected: selected.has(option.id),
        voteCount: resultsVisible
          ? typeof safeRemoteCounts[option.id] === 'number'
            ? safeRemoteCounts[option.id] as number
            : allIntents.filter((intent) => intent.payload.optionId === option.id).length
          : null,
      })),
    }
  }

  async listPollViews(memberId: string, isAdmin: boolean, now = new Date()): Promise<PollView[]> {
    const polls = await this.listPolls(now)
    return Promise.all(polls.map((poll) => this.getPollView(poll.id, memberId, isAdmin, now)))
  }

  async closePoll(pollId: string): Promise<void> {
    await this.#setStatus(pollId, 'closed')
  }

  async revealPoll(pollId: string): Promise<void> {
    const pollEntity = await this.#db.entities.get(`poll:${pollId}`)
    if (!pollEntity) throw new Error('Vote introuvable.')
    const status = pollStatusAt(fromEntity(pollEntity), new Date())
    if (status === 'open') throw new Error('Clôturez le vote avant la révélation.')
    await this.#setStatus(pollId, 'revealed')
  }

  async #setStatus(pollId: string, status: PollStatus): Promise<void> {
    const entity = await this.#db.entities.get(`poll:${pollId}`)
    if (!entity) throw new Error('Vote introuvable.')
    await this.#local.mutate({
      mutationId: crypto.randomUUID(),
      entityType: 'poll',
      entityId: pollId,
      tripId: entity.tripId,
      operation: 'update',
      baseVersion: entity.version,
      payload: { ...entity.payload, status },
      createdAtDevice: new Date().toISOString(),
    })
  }

  async #intentEntities(pollId: string, memberId: string): Promise<LocalEntity[]> {
    return this.#db.entities.where('entityType').equals('vote_intent')
      .filter((entity) => entity.payload.pollId === pollId && entity.payload.memberId === memberId)
      .toArray()
  }

  async #writeIntent(input: {
    poll: PollRecord
    memberId: string
    optionId: string
    selected: boolean
    entityId: string
    mutationId: string
    now: Date
  }): Promise<void> {
    const existing = await this.#db.entities.get(`vote_intent:${input.entityId}`)
    await this.#local.mutate({
      mutationId: input.mutationId,
      entityType: 'vote_intent',
      entityId: input.entityId,
      tripId: input.poll.tripId,
      operation: 'upsert',
      baseVersion: existing?.version ?? 0,
      payload: {
        pollId: input.poll.id,
        optionId: input.optionId,
        memberId: input.memberId,
        selected: input.selected,
        updatedAtDevice: input.now.toISOString(),
      },
      createdAtDevice: input.now.toISOString(),
    })
  }
}
